import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import "./styles/PageHeader.css";

const SITE_NAME = "Rothenburgschule";

/**
 * Breadcrumb + H1 + subtitle. Used on 19 of 21 pages.
 *
 * `parent` is either { label, to } or omitted for a top-level page.
 * Internal links go through <Link> so they no longer full-reload the
 * app — the old pages used raw <a href="/unterricht">.
 *
 * Also sets the browser/Google title ("Termine | Rothenburgschule") and
 * the meta description (from the subtitle) for the current page, and
 * restores the defaults from index.html when the page is left.
 *
 * <PageHeader
 *   parent={{ label: "Lernwelt", to: "/lernen-und-verstehen" }}
 *   current="Sprachen stärken"
 *   title={heroTitle}
 *   subtitle={heroSubtitle}
 * />
 */
export default function PageHeader({ parent, current, title, subtitle }) {
  useDocumentMeta(title || current, subtitle);

  return (
    <header className="rs-header">
      <div className="container">
        {(parent || current) && (
          <nav className="rs-breadcrumb" aria-label="Breadcrumb">
            {parent &&
              (parent.to ? (
                <Link to={parent.to}>{parent.label}</Link>
              ) : (
                <span>{parent.label}</span>
              ))}
            {parent && current && (
              <span className="rs-breadcrumb__sep" aria-hidden="true">
                /
              </span>
            )}
            {current && <span aria-current="page">{current}</span>}
          </nav>
        )}

        <div className="rs-headline">
          <h1>{title}</h1>
          {subtitle && <p>{subtitle}</p>}
        </div>
      </div>
    </header>
  );
}

/** Sets <title> and <meta name="description"> for the current page. */
function useDocumentMeta(pageTitle, description) {
  useEffect(() => {
    const metaDesc = document.querySelector('meta[name="description"]');
    const defaultTitle = document.title;
    const defaultDesc = metaDesc?.getAttribute("content") ?? "";

    if (pageTitle) {
      document.title = `${pageTitle} | ${SITE_NAME}`;
    }

    if (metaDesc && description) {
      // Google shows about 150–160 characters
      const text = String(description).replace(/\s+/g, " ").trim();
      metaDesc.setAttribute("content", text.length > 160 ? `${text.slice(0, 157)}…` : text);
    }

    return () => {
      document.title = defaultTitle;
      if (metaDesc) metaDesc.setAttribute("content", defaultDesc);
    };
  }, [pageTitle, description]);
}